<template>
    <vx-expandable-card class="mb-4">
        <template v-slot:header><span :id="paymentMethodLabelId">Payment Method & Terms</span></template>
        <template v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <div
                    v-if="team.parent_id"
                    role="radiogroup"
                    :aria-labelledby="paymentMethodLabelId"
                    class="flex flex-col gap-2"
                    data-test="payment-methods"
                >
                    <label class="flex items-center gap-2 pb-2">
                        <input
                            type="radio"
                            v-model="billingOption"
                            :name="billingOptionName"
                            value="parent"
                            :disabled="team.status === 'closed'"
                            class="w-4 h-4"
                        />
                        <span>Parent team pays</span>
                    </label>
                    <label class="flex items-center gap-2 pb-2">
                        <input
                            type="radio"
                            v-model="billingOption"
                            :name="billingOptionName"
                            value="automatic"
                            :disabled="team.status === 'closed'"
                            class="w-4 h-4"
                        />
                        <span>Automatic card payment (due immediately)</span>
                    </label>
                    <div class="flex items-start gap-2 pb-2">
                        <input
                            type="radio"
                            :id="invoiceOptionId"
                            v-model="billingOption"
                            :name="billingOptionName"
                            value="invoice"
                            :disabled="team.status === 'closed'"
                            class="w-4 h-4 mt-1"
                        />
                        <div class="flex flex-col gap-2 flex-1">
                            <label :for="invoiceOptionId">Invoice with payment terms</label>
                            <div v-if="billingOption === 'invoice'" class="flex items-center gap-2">
                                <span :id="paymentTermsLabelId" class="text-sm">Payment terms:</span>
                                <vx-input
                                    v-model="form.payment_terms_days"
                                    :aria-labelledby="paymentTermsLabelId"
                                    :aria-describedby="
                                        paymentTermsError
                                            ? `${paymentTermsHintId} ${paymentTermsErrorId}`
                                            : paymentTermsHintId
                                    "
                                    type="number"
                                    :min="1"
                                    :max="120"
                                    size="sm"
                                    class="w-20"
                                    :disabled="team.status === 'closed'"
                                    data-test="payment-terms-input"
                                    @blur="validatePaymentTerms"
                                />
                                <span class="text-sm">days</span>
                            </div>
                            <span
                                v-if="billingOption === 'invoice'"
                                :id="paymentTermsHintId"
                                class="text-xs text-gray-500"
                            >
                                e.g., 30 for Net 30, 60 for Net 60 (max 120 days)
                            </span>
                            <span
                                v-if="paymentTermsError && billingOption === 'invoice'"
                                :id="paymentTermsErrorId"
                                class="text-xs text-red-600"
                            >
                                {{ paymentTermsError }}
                            </span>
                        </div>
                    </div>
                </div>

                <div
                    v-if="!team.parent_id"
                    role="radiogroup"
                    :aria-labelledby="paymentMethodLabelId"
                    class="flex flex-col gap-2"
                    data-test="payment-methods"
                >
                    <label class="flex items-center gap-2 pb-2">
                        <input
                            type="radio"
                            v-model="rootTeamBillingOption"
                            :name="rootTeamBillingOptionName"
                            value="automatic"
                            :disabled="team.status === 'closed'"
                            class="w-4 h-4"
                        />
                        <span>Automatic card payment (due immediately)</span>
                    </label>
                    <div class="flex items-start gap-2 pb-2">
                        <input
                            type="radio"
                            :id="invoiceOptionId"
                            v-model="rootTeamBillingOption"
                            :name="rootTeamBillingOptionName"
                            value="invoice"
                            :disabled="team.status === 'closed'"
                            class="w-4 h-4 mt-1"
                        />
                        <div class="flex flex-col gap-2 flex-1">
                            <label :for="invoiceOptionId">Invoice with payment terms</label>
                            <div v-if="rootTeamBillingOption === 'invoice'" class="flex items-center gap-2">
                                <span :id="paymentTermsLabelId" class="text-sm">Payment terms:</span>
                                <vx-input
                                    v-model="form.payment_terms_days"
                                    :aria-labelledby="paymentTermsLabelId"
                                    :aria-describedby="
                                        paymentTermsError
                                            ? `${paymentTermsHintId} ${paymentTermsErrorId}`
                                            : paymentTermsHintId
                                    "
                                    type="number"
                                    :min="1"
                                    :max="120"
                                    size="sm"
                                    class="w-20"
                                    :disabled="team.status === 'closed'"
                                    data-test="payment-terms-input"
                                    @blur="validatePaymentTerms"
                                />
                                <span class="text-sm">days</span>
                            </div>
                            <span
                                v-if="rootTeamBillingOption === 'invoice'"
                                :id="paymentTermsHintId"
                                class="text-xs text-gray-500"
                            >
                                e.g., 30 for Net 30, 60 for Net 60 (max 120 days)
                            </span>
                            <span
                                v-if="paymentTermsError && rootTeamBillingOption === 'invoice'"
                                :id="paymentTermsErrorId"
                                class="text-xs text-red-600"
                            >
                                {{ paymentTermsError }}
                            </span>
                        </div>
                    </div>
                </div>

                <div v-if="team.status !== 'closed'">
                    <vx-button
                        data-test="submit-button"
                        @click="submit"
                        size="lg"
                        color="primary"
                        :loading="submitting"
                        :disabled="!!paymentTermsError"
                        class="w-full"
                    >
                        Update
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { computed, ref, useId, watch } from 'vue';
import { VxButton, VxExpandableCard, VxInput } from '@voxie/frontend-components';
import { adminTeamsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const props = defineProps({
    team: {
        type: Object,
        required: true,
    },
    teamId: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['updated', 'unsavedChanges']);

const toasted = useToasted();

const form = ref({
    is_billable: true,
    payment_terms_days: '0',
});

const submitting = ref(false);
const paymentTermsError = ref(null);
const billingOption = ref('parent');
const rootTeamBillingOption = ref('automatic');

const paymentMethodLabelId = useId();
const billingOptionName = useId();
const rootTeamBillingOptionName = useId();
const invoiceOptionId = useId();
const paymentTermsLabelId = useId();
const paymentTermsHintId = useId();
const paymentTermsErrorId = useId();

const initializeForm = () => {
    form.value = {
        is_billable: props.team.parent_id ? props.team.is_billable : true,
        payment_terms_days: String(props.team.payment_terms_days || 0),
    };

    if (props.team.parent_id) {
        if (!props.team.is_billable) {
            billingOption.value = 'parent';
        } else if (props.team.payment_terms_days === 0) {
            billingOption.value = 'automatic';
        } else {
            billingOption.value = 'invoice';
        }
    } else {
        rootTeamBillingOption.value = props.team.payment_terms_days > 0 ? 'invoice' : 'automatic';
    }
};

watch(
    () => props.team,
    (newTeam) => {
        if (newTeam) {
            initializeForm();
        }
    },
    { immediate: true }
);

watch(billingOption, (newValue) => {
    if (!props.team?.parent_id) {
        return;
    }

    if (newValue === 'parent') {
        form.value.is_billable = false;
        form.value.payment_terms_days = '0';
    } else if (newValue === 'automatic') {
        form.value.is_billable = true;
        form.value.payment_terms_days = '0';
    } else if (newValue === 'invoice') {
        form.value.is_billable = true;
        if (form.value.payment_terms_days === '0' || form.value.payment_terms_days === 0) {
            form.value.payment_terms_days = '30';
        }
    }
});

watch(rootTeamBillingOption, (newValue) => {
    if (props.team?.parent_id) {
        return;
    }

    if (newValue === 'automatic') {
        form.value.payment_terms_days = '0';
    } else if (
        newValue === 'invoice' &&
        (form.value.payment_terms_days === '0' || form.value.payment_terms_days === 0)
    ) {
        form.value.payment_terms_days = '30';
    }
});

const payload = computed(() => ({
    is_billable: props.team.parent_id ? form.value.is_billable : true,
    payment_terms_days: parseInt(form.value.payment_terms_days) || 0,
}));

const unsavedChanges = computed(
    () =>
        submitting.value ||
        payload.value.is_billable !== (props.team.parent_id ? props.team.is_billable : true) ||
        payload.value.payment_terms_days !== (props.team.payment_terms_days || 0)
);

watch(unsavedChanges, (value) => emit('unsavedChanges', value));

const validatePaymentTerms = () => {
    const days = parseInt(form.value.payment_terms_days) || 0;
    if (days > 0) {
        if (days > 120) {
            paymentTermsError.value = 'Payment terms cannot exceed 120 days';
        } else {
            paymentTermsError.value = null;
        }
    } else {
        paymentTermsError.value = null;
    }
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    validatePaymentTerms();
    if (paymentTermsError.value) {
        return;
    }

    submitting.value = true;

    try {
        const data = payload.value;

        await adminTeamsClient.updateBillingOptions(props.teamId, data);

        emit('updated', data);

        toasted.success('Payment method & terms have been successfully updated');
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};
</script>
